import { ArrowLeft, RotateCw, SaveIcon, TriangleAlert } from 'lucide-react';
import { FC, useEffect } from "react";
import z from 'zod';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { S3FileUploader, Spinner } from '@/modules/common';
import ErrorComponent from '@/modules/common/components/error-component';

import { UnsavedChangesAlertDialog } from '../components';
import { EventDetailsTab, EVENT_DETAILS_HISTORY_TABS, EVENT_DETAILS_TABLE_TABS } from "../constants";
import { useEventDetailsHandler, useEventDetailsTabs } from '../hooks';
import { EventFormSchema } from '../validations';
import { cn } from '@/lib/utils';

export type EventFormSchemaType = z.infer<typeof EventFormSchema>;

export const EventDetailsV2Page: FC = () => {
  const {
    details,
    error,
    form,
    filesConfig,
    isFetching,
    unsavedChangesModalProps,
    isUpdateLoading,
    disabledSaveButton,
    skipUpload,
    refetchEvent,
    handleFormSubmit,
    onReset,
    refetchUploadCredentials,
  } = useEventDetailsHandler();

  const {
    tabsConfig,
    activeTab,
    visibleTabs,
    isFetchingTypes,
    onChangeTab,
    onBack,
  } = useEventDetailsTabs({ selectedTypes: details?.types || [] });

  const isHistoryTab = EVENT_DETAILS_HISTORY_TABS.includes(activeTab);
  const isTableTab = EVENT_DETAILS_TABLE_TABS.includes(activeTab);

  const isTabWarning = (tab: EventDetailsTab) => {
    const validations = Object.keys(details.validationErrors);
    return validations.includes(tab);
  }

  if (isFetchingTypes || isFetching) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorComponent message={error.message} onRetry={refetchEvent} />;
  }

  return (
    <div className="pb-[100px]">
      <header className="sticky top-0 left-0 z-9  h-14 flex w-full items-center border-b bg-background px-2">
        <div className="flex items-center">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft />
          </Button>
          <p className="font-bold text-lg text-zinc-50">{details?.name}</p>
        </div>

        <div className="flex flex-1 items-center" />
      </header>
      <div className="p-4">
        <Form {...form}>
          <Tabs
            defaultValue={EventDetailsTab.Info}
            value={activeTab}
            onValueChange={(tab: EventDetailsTab) => onChangeTab(tab)}
          >
            <TabsList className="border-none">
              {tabsConfig.map((tab) => {
                if (!visibleTabs.includes(tab.value)) {
                  return null;
                }
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="min-w-none text-sm gap-1"
                  >
                    {isTabWarning(tab.value) && <TriangleAlert size={16} className="text-error-700" />}
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {tabsConfig.map(({ value, title, Component }) => {
              return (
                <TabsContent key={value} value={value}>
                  <S3FileUploader
                    config={filesConfig}
                    onComplete={handleFormSubmit}
                    refetchCredentials={refetchUploadCredentials}
                    //update logic
                    skipUpload={skipUpload}
                  >
                    {(props) => (
                      <>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            props.uploadFiles();
                          }}
                          id="event-form"
                        >
                          <div className="flex flex-col gap-5">
                            <div className={cn(!isTableTab && 'border rounded-lg border-zinc-800 p-6')}>
                              <h3 className="text-lg font-semibold mb-4">
                                {title}
                              </h3>
                              <Component {...props} />
                            </div>
                          </div>
                          <UnsavedChangesAlertDialog
                            {...unsavedChangesModalProps}
                          />

                          {!isHistoryTab && (
                            <div className="w-full flex  gap-4 justify-end fixed bottom-0 left-0 border-t border-zinc-800 bg-zinc-950 p-4 z-10">
                              <Button
                                className="w-fit self-end"
                                variant="outline"
                                disabled={disabledSaveButton}
                                startIcon={<RotateCw />}
                                onClick={onReset}
                              >
                                Reset changes
                              </Button>
                              <Button
                                className="w-fit self-end"
                                type="submit"
                                form="event-form"
                                isLoading={isUpdateLoading}
                                startIcon={<SaveIcon />}
                                disabled={disabledSaveButton}
                              >
                                Save changes
                              </Button>
                            </div>
                          )}
                        </form>
                      </>
                    )}
                  </S3FileUploader>
                </TabsContent>
              );
            })}
          </Tabs>
        </Form>
      </div>
    </div>
  );
};
